import { useEffect, useMemo, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";

const varsOf = (text) => [...new Set((String(text || "").match(/\{\{(\d+)\}\}/g) || []).map((v) => Number(v.replace(/\D/g, ""))))].sort((a, b) => a - b);

function parse(t) {
  const comps = t?.components || [];
  const body = comps.find((c) => c.type === "BODY");
  const header = comps.find((c) => c.type === "HEADER");
  const btns = (comps.find((c) => c.type === "BUTTONS")?.buttons || []).map((b, index) => ({ ...b, index }));
  return {
    body: body?.text || "", bodyVars: varsOf(body?.text),
    headerText: header?.format === "TEXT" ? header.text : "", headerVar: header?.format === "TEXT" && varsOf(header.text).length > 0,
    headerMedia: header && header.format !== "TEXT",
    urlBtns: btns.filter((b) => b.type === "URL" && /\{\{\d+\}\}/.test(b.url || "")),
  };
}

export function TemplateDialog({ open, onClose, phone, onSent }) {
  const [tpls, setTpls] = useState(null);
  const [name, setName] = useState("");
  const [vals, setVals] = useState({});
  const [hdr, setHdr] = useState("");
  const [btn, setBtn] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || tpls) return;
    api.get("/whatsapp/templates/all").then(({ data }) => setTpls(data.filter((t) => t.status === "APPROVED"))).catch((e) => { setTpls([]); toast.error(apiError(e.response?.data?.detail)); });
  }, [open, tpls]);
  const t = (tpls || []).find((x) => `${x.name}|${x.language}` === name);
  const p = useMemo(() => parse(t), [t]);
  const preview = p.body.replace(/\{\{(\d+)\}\}/g, (m, n) => vals[n] || m);
  const ready = t && p.bodyVars.every((n) => (vals[n] || "").trim()) && (!p.headerVar || hdr.trim()) && p.urlBtns.every((b) => (btn[b.index] || "").trim());

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/wa-inbox/conversations/${phone}/send-template`, {
        template_name: t.name, language: t.language, body_params: p.bodyVars.map((n) => vals[n]),
        header_param: p.headerVar ? hdr : null, button_params: p.urlBtns.map((b) => ({ index: b.index, value: btn[b.index] })),
      });
      toast.success("Template sent"); setVals({}); setHdr(""); setBtn({}); onSent(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inbox-template-dialog">
        <DialogHeader><DialogTitle>Send a template</DialogTitle></DialogHeader>
        {!tpls ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00a884]" /></div> : (
          <div className="space-y-3">
            <select value={name} onChange={(e) => { setName(e.target.value); setVals({}); setBtn({}); setHdr(""); }} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="inbox-template-select">
              <option value="">Choose an approved template…</option>
              {tpls.map((x) => <option key={`${x.name}|${x.language}`} value={`${x.name}|${x.language}`}>{x.name} ({x.language})</option>)}
            </select>
            {t && <>
              {p.headerVar && <input value={hdr} onChange={(e) => setHdr(e.target.value)} placeholder={`Header: ${p.headerText}`} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="inbox-template-header-input" />}
              {p.bodyVars.map((n) => (
                <input key={n} value={vals[n] || ""} onChange={(e) => setVals({ ...vals, [n]: e.target.value })} placeholder={`Value for {{${n}}}`} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`inbox-template-var-${n}`} />
              ))}
              {p.urlBtns.map((b) => (
                <input key={b.index} value={btn[b.index] || ""} onChange={(e) => setBtn({ ...btn, [b.index]: e.target.value })} placeholder={`Link for button "${b.text}"`} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid={`inbox-template-btn-${b.index}`} />
              ))}
              {p.headerMedia && <p className="text-xs text-amber-600">This template has a media header — it uses the media saved with the template.</p>}
              <div className="rounded-lg p-3" style={{ background: "#efeae2" }}>
                <div className="bg-[#d9fdd3] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap shadow-sm" data-testid="inbox-template-preview">{preview || "(no body text)"}</div>
              </div>
            </>}
            <button onClick={send} disabled={!ready || busy} className="w-full bg-[#00a884] hover:bg-[#008f72] disabled:opacity-50 text-white font-medium rounded-lg py-2.5 flex items-center justify-center gap-2" data-testid="inbox-template-send-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send template
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
